import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { supabase } from './supabase';
import { queryClient } from './queryClient';

/** Native Preferences key used by Capacitor bridge */
export const AUTH_TOKEN_KEY = 'authToken';

const DEFAULT_PAGE_KEY = 'defaultPage';

/** Blocks Landing auto-login after logout until the user intentionally signs in again. */
const LOGOUT_GUARD_KEY = 'mt_logout_guard';
const LOGOUT_GUARD_TTL_MS = 5 * 60 * 1000;

/**
 * Device-level prefs that should survive logout / account switch.
 * Everything else in localStorage is treated as user-scoped and wiped.
 */
const DEVICE_PREF_KEYS = new Set([
  'theme',
  'theme_color',
  'language',
  'settings_water_gyro',
  DEFAULT_PAGE_KEY,
]);

function shouldPreserveLocalStorageKey(key: string) {
  if (DEVICE_PREF_KEYS.has(key)) return true;
  // Per-user default home: defaultPage:<userId>
  if (key.startsWith(`${DEFAULT_PAGE_KEY}:`)) return true;
  return false;
}

export function markLogoutGuard() {
  try {
    sessionStorage.setItem(LOGOUT_GUARD_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearLogoutGuard() {
  try {
    sessionStorage.removeItem(LOGOUT_GUARD_KEY);
  } catch {
    /* ignore */
  }
}

export function isLogoutGuardActive(): boolean {
  try {
    const raw = sessionStorage.getItem(LOGOUT_GUARD_KEY);
    if (!raw) return false;
    const ts = Number.parseInt(raw, 10);
    if (!Number.isFinite(ts)) return true;
    if (Date.now() - ts > LOGOUT_GUARD_TTL_MS) {
      sessionStorage.removeItem(LOGOUT_GUARD_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Remove Supabase auth persistence keys so a restored session cannot auto-login. */
function purgeSupabaseAuthStorage() {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith('sb-') || key.toLowerCase().includes('supabase')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      console.log(`🔒 Supabase auth keys purged (${keysToRemove.length})`);
    }
  } catch (e) {
    console.warn('[AuthSync] Supabase auth purge failed:', e);
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Auth operation timeout')), ms),
  );
  return Promise.race([promise, timeout]);
}

let establishSessionInFlight: Promise<any> | null = null;

/**
 * Wipe user-scoped web storage so the next account cannot inherit UI / finance prefs.
 * Keeps only true device preferences (theme / language / default page / etc.).
 */
export function clearUserScopedDeviceData(options?: { preserveSessionToken?: boolean }) {
  const preservedToken = options?.preserveSessionToken
    ? localStorage.getItem('sessionToken')
    : null;

  let logoutGuard: string | null = null;
  try {
    logoutGuard = sessionStorage.getItem(LOGOUT_GUARD_KEY);
    sessionStorage.clear();
    if (logoutGuard) {
      sessionStorage.setItem(LOGOUT_GUARD_KEY, logoutGuard);
    }
  } catch (e) {
    console.warn('[AuthSync] sessionStorage.clear failed:', e);
  }

  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (shouldPreserveLocalStorageKey(key)) continue;
      keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    console.log(`🔒 User-scoped localStorage cleared (${keysToRemove.length} keys)`);
  } catch (e) {
    console.warn('[AuthSync] localStorage user wipe failed:', e);
  }

  if (preservedToken) {
    localStorage.setItem('sessionToken', preservedToken);
  }
}

/**
 * Drop cached financial queries. Optionally keep the auth user query mounted.
 */
export function clearClientQueryCache(options?: { keepAuthUser?: boolean }) {
  if (options?.keepAuthUser) {
    queryClient.removeQueries({
      predicate: (query) => query.queryKey?.[0] !== '/api/auth/user',
    });
  } else {
    queryClient.clear();
  }
  console.log('🔒 Query Cache Cleared');
}

/** Resolve the home path for a user (per-user override → legacy device key → overview). */
export function resolveDefaultHomePath(userId?: string | null): string {
  if (userId) {
    const perUser = localStorage.getItem(`${DEFAULT_PAGE_KEY}:${userId}`);
    if (perUser && perUser.startsWith('/')) return perUser;
  }
  const legacy = localStorage.getItem(DEFAULT_PAGE_KEY);
  if (legacy && legacy.startsWith('/')) return legacy;
  return '/';
}

export function saveDefaultHomePath(userId: string | undefined | null, path: string) {
  if (!path.startsWith('/')) return;
  localStorage.setItem(DEFAULT_PAGE_KEY, path);
  if (userId) {
    localStorage.setItem(`${DEFAULT_PAGE_KEY}:${userId}`, path);
  }
}

/**
 * After login: auth cache is already set — Router will swap Landing → app shell.
 * Do NOT full-page reload (that caused repeat loading with Supabase SIGNED_IN).
 */
export function enterAuthenticatedApp(_path?: string) {
  // Intentionally a no-op for navigation. Callers rely on setQueryData(['/api/auth/user']).
  // Default-page routing is handled once in App Router after authentication.
  console.log('✅ [AuthSync] Session ready — waiting for Router to enter app shell');
}

/**
 * Establish a brand-new client session after login / OAuth sync.
 * Token first → wipe prior user data → verify session → pin user in React Query.
 */
export async function establishClientSession(sessionToken: string) {
  if (establishSessionInFlight) {
    return establishSessionInFlight;
  }

  establishSessionInFlight = (async () => {
    // Intentional login — allow SIGNED_IN / session restore paths again
    clearLogoutGuard();

    // 1) Install token before any refetch can run
    localStorage.setItem('sessionToken', sessionToken);

    // 2) Wipe prior-user prefs; keep the new token (+ preserved default page keys)
    clearUserScopedDeviceData({ preserveSessionToken: true });

    // 3) Drop financial caches but keep auth observer stable
    clearClientQueryCache({ keepAuthUser: true });

    await syncAuthToNative(sessionToken);

    // 4) Force a fresh auth user fetch and pin it into the cache
    const user = await queryClient.fetchQuery({
      queryKey: ['/api/auth/user'],
      staleTime: 0,
    });

    if (!user) {
      throw new Error('登入成功，但無法驗證工作階段，請再試一次');
    }

    queryClient.setQueryData(['/api/auth/user'], user);
    // Mark that this browser session already did the post-login home redirect setup
    try {
      sessionStorage.setItem('pendingDefaultHomeRedirect', '1');
    } catch {
      /* ignore */
    }
    console.log('✅ Client session established for new login');
    return user;
  })();

  try {
    return await establishSessionInFlight;
  } finally {
    establishSessionInFlight = null;
  }
}

/**
 * Comprehensive Logout
 *
 * Order matters: local Supabase signOut must finish (or time out) *before* wiping
 * storage. Otherwise an in-flight signOut can rewrite sb-* keys after we clear them,
 * and Landing's SIGNED_IN handler will silently log the user back in.
 */
export async function performComprehensiveLogout() {
  console.log('🔒 Initiating Comprehensive Logout...');

  const sessionToken = localStorage.getItem('sessionToken');

  // Stop Landing auto-login immediately (survives sessionStorage.clear via preserve).
  markLogoutGuard();
  queryClient.setQueryData(['/api/auth/user'], null);

  // 1) Drop Supabase session locally first — avoids post-wipe persistence races.
  try {
    await withTimeout(supabase.auth.signOut({ scope: 'local' }), 3000);
    console.log('🔒 Supabase local SignOut completed');
  } catch (e) {
    console.warn('🔒 Supabase local SignOut timed out/failed (continuing cleanup):', e);
  }

  // 2) Invalidate backend session (best effort).
  try {
    await withTimeout(
      fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
        },
      }).then((res) => {
        if (!res.ok) console.warn('🔒 Backend Logout status:', res.status);
      }),
      3000,
    );
    console.log('🔒 Backend Logout Completed');
  } catch (e) {
    console.warn('🔒 Backend Logout Timed out or Failed (Proceeding to Local Cleanup):', e);
  }

  // 3) Wipe client caches + user-scoped storage.
  clearClientQueryCache();
  clearUserScopedDeviceData();
  purgeSupabaseAuthStorage();

  ['sessionToken', 'cap_auth_token_backup', 'sb-access-token', 'sb-refresh-token', 'supabase.auth.token'].forEach(
    (k) => localStorage.removeItem(k),
  );

  markLogoutGuard();

  try {
    await withTimeout(clearNativeAuth(), 1000);
  } catch (e) {
    console.warn('🔒 Native Storage Clear Timed out (Ignoring)', e);
  }

  document.cookie = 'cap_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax';
  document.cookie = 'sessionId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax';

  // 4) Final pass — anything that rehydrated mid-cleanup gets dropped again.
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    /* ignore */
  }
  purgeSupabaseAuthStorage();
  markLogoutGuard();
  queryClient.setQueryData(['/api/auth/user'], null);

  console.log('✅ Comprehensive Logout Complete.');
}

export async function syncAuthToNative(token: string) {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await Preferences.set({
      key: AUTH_TOKEN_KEY,
      value: token,
    });
    console.log('[AuthSync] Token synced to Native Preferences');

    document.cookie = `cap_auth_token=${token}; max-age=31536000; path=/`;
    console.log('[AuthSync] Token synced to Cookie (Fallback)');
  } catch (error) {
    console.warn('[AuthSync] Failed to sync token to Preferences:', error);

    try {
      console.log('[AuthSync] Setting fallback cookie & localStorage...');
      document.cookie = `cap_auth_token=${token}; max-age=31536000; path=/; SameSite=Lax`;
      localStorage.setItem('cap_auth_token_backup', token);
      console.log('[AuthSync] Token synced to localStorage (Backup for Native Bridge)');
    } catch (e) {
      console.error('[AuthSync] Error setting fallback storage:', e);
    }
  }
}

export async function clearNativeAuth() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const keys = [AUTH_TOKEN_KEY, 'CapacitorStorage.authToken'];
  for (const key of keys) {
    try {
      await Preferences.remove({ key });
      console.log(`[AuthSync] Token cleared from Native Preferences (${key})`);
    } catch (error) {
      console.warn(`[AuthSync] Failed to clear native key ${key}:`, error);
    }
  }
}
